import moment from 'moment';

import {
  PROPERTY_TYPE_OPTION_HOME,
  PROPERTY_TYPE_OPTION_APARTMENT,
  PROPERTY_TYPE_OPTION_TOWNHOMES_DUPLEXES,
} from 'common/propertyTypes';

import log from 'client/lib/log';

const debug = log.debug('sunroom:leadTypeHelpers');

export const LEAD_TYPE_HOME = 'home';
export const LEAD_TYPE_APARTMENT = 'apartment';
export const LEAD_TYPE_FUTURE_HOME = 'future home';
export const LEAD_TYPE_FUTURE_APARTMENT = 'future apartment';
export const LEAD_TYPE_FUTURE_LIST = [LEAD_TYPE_FUTURE_HOME, LEAD_TYPE_FUTURE_APARTMENT];

export const HOME_LEAD_MOVE_IN_DATE_CUTOFF = 60;
export const APARTMENT_LEAD_MOVE_IN_DATE_CUTOFF = 100;

export function getLeadType(user) {
  const userMoveInMoment = moment(user.moveInDate);

  const hasApartmentFilters =
    user.searchFilters.propertyTypes.includes(PROPERTY_TYPE_OPTION_APARTMENT) &&
    !user.searchFilters.propertyTypes.includes(PROPERTY_TYPE_OPTION_HOME);
  const hasHomeFilters =
    user.searchFilters.propertyTypes.includes(PROPERTY_TYPE_OPTION_HOME) ||
    !user.searchFilters.propertyTypes.includes(PROPERTY_TYPE_OPTION_APARTMENT);

  if (hasApartmentFilters || isExtraLowBudgetHomeLead(user)) {
    const moveInDateCutoffMoment = moment().add(APARTMENT_LEAD_MOVE_IN_DATE_CUTOFF, 'days');
    return userMoveInMoment.isBefore(moveInDateCutoffMoment)
      ? LEAD_TYPE_APARTMENT
      : LEAD_TYPE_FUTURE_APARTMENT;
  }
  if (hasHomeFilters) {
    const moveInDateCutoffMoment = moment().add(HOME_LEAD_MOVE_IN_DATE_CUTOFF, 'days');
    return userMoveInMoment.isBefore(moveInDateCutoffMoment)
      ? LEAD_TYPE_HOME
      : LEAD_TYPE_FUTURE_HOME;
  }

  throw new Error(`Unknow lead type for user ${user.id}`);
}

export function isHomeLead(user) {
  return getLeadType(user) === LEAD_TYPE_HOME;
}

export function isFutureHomeLead(user) {
  return getLeadType(user) === LEAD_TYPE_FUTURE_HOME;
}

export function isApartmentLead(user) {
  return getLeadType(user) === LEAD_TYPE_APARTMENT;
}

export function isFutureApartmentLead(user) {
  return getLeadType(user) === LEAD_TYPE_FUTURE_APARTMENT;
}

export function isFutureLead(user) {
  return LEAD_TYPE_FUTURE_LIST.includes(getLeadType(user));
}

export function isUnreasonableApartmentLead(user) {
  return (
    user.searchFilters.maxRentPrice &&
    user.searchFilters.maxRentPrice <= 1000 &&
    Math.min(...user.searchFilters.bedrooms) > 1
  );
}

export function isExtraLowBudgetHomeLead(user) {
  return (
    user.searchFilters.maxRentPrice <= 1000 &&
    !user.searchFilters.propertyTypes.includes(PROPERTY_TYPE_OPTION_APARTMENT) &&
    user.searchFilters.propertyTypes.includes(PROPERTY_TYPE_OPTION_TOWNHOMES_DUPLEXES)
  );
}

export function isLowBudgetHomeLead(user) {
  return (
    !isApartmentLead(user) &&
    user.searchFilters.maxRentPrice &&
    user.searchFilters.maxRentPrice <= 1250
  );
}

export function isHighBudgetHomeLead(user) {
  return (
    !isApartmentLead(user) &&
    (user.searchFilters.maxRentPrice === null || user.searchFilters.maxRentPrice >= 1500)
  );
}

export function shouldUserSwitchToApartments(user) {
  const userLeadType = getLeadType(user);

  const isUserExtraLowBudgetHomeLead = isExtraLowBudgetHomeLead(user);
  const isUserLowBudgetHomeLead = isLowBudgetHomeLead(user);
  const isUserHighBudgetHomeLead = isHighBudgetHomeLead(user);

  const shouldSwitchToApartments =
    (userLeadType === LEAD_TYPE_HOME || userLeadType === LEAD_TYPE_FUTURE_HOME) &&
    !isUserExtraLowBudgetHomeLead &&
    (isLowBudgetHomeLead || isUserHighBudgetHomeLead);

  debug('shouldUserSwitchToApartments', {
    leadType: userLeadType,
    isExtraLowBudgetHomeLead: isUserExtraLowBudgetHomeLead,
    isLowBudgetHomeLead: isUserLowBudgetHomeLead,
    isHighBudgetHomeLead: isUserHighBudgetHomeLead,
    shouldSwitchToApartments: shouldSwitchToApartments,
  });

  return shouldSwitchToApartments;
}
